import { useState, type ReactNode } from "react";
import type { DoorUnlockPhase } from "./door-unlock-machine";
import { DoorUnlockStatusPanel, type DoorUnlockInterruption } from "./DoorUnlockStatusPanel";
import { useDoorUnlockPolling, type DoorUnlockOutcome, type DoorUnlockTarget } from "./useDoorUnlockPolling";

export type { DoorUnlockPhase } from "./door-unlock-machine";
export type { DoorUnlockOutcome, DoorUnlockTarget } from "./useDoorUnlockPolling";

type DoorUnlockPollingProps = {
	unlockRequestId: string;
	accessToken?: string;
	target: DoorUnlockTarget;
	onTerminate: (outcome: DoorUnlockOutcome) => void;
	onExit?: () => void;
	children?: (state: { phase: DoorUnlockPhase }) => ReactNode;
};

export function DoorUnlockPolling(props: DoorUnlockPollingProps) {
	return <DoorUnlockPollingSession key={`${props.unlockRequestId}:${props.target}`} {...props} />;
}

function DoorUnlockPollingSession(props: DoorUnlockPollingProps) {
	const { unlockRequestId, accessToken, target, onTerminate, onExit, children } = props;
	const [terminalOutcome, setTerminalOutcome] = useState<Extract<DoorUnlockOutcome, { type: "timed_out" | "error" }>>();
	const [retryKey, setRetryKey] = useState(0);
	const polling = useDoorUnlockPolling({
		unlockRequestId,
		accessToken,
		target,
		retryKey,
		onTerminate: (outcome) => {
			if (outcome.type === "timed_out" || outcome.type === "error") {
				setTerminalOutcome(outcome);
				return;
			}
			onTerminate(outcome);
		},
	});
	let interruption: DoorUnlockInterruption | undefined;
	if (polling.hubStatus === "OFFLINE") {
		interruption = polling.phase === "waiting_for_close" ? "reconnecting" : "offline";
	} else if (terminalOutcome) interruption = "failed";
	else if (polling.connection === "unavailable") {
		interruption = polling.phase === "waiting_for_close" ? "reconnecting" : "offline";
	} else if (polling.connection === "recovering") {
		interruption = polling.phase === "waiting_for_close" ? "reconnecting" : "checking";
	} else if (polling.isDelayed) interruption = "checking";

	if (interruption) {
		return (
			<DoorUnlockStatusPanel
				state={interruption}
				onExit={polling.phase === "waiting_for_close" ? onExit : undefined}
				onRetry={() => {
					setTerminalOutcome(undefined);
					setRetryKey((current) => current + 1);
				}}
			/>
		);
	}

	return children?.({ phase: polling.phase });
}
